import { useQuery } from "@tanstack/react-query";
import {
  CountryCode,
  InfrastructureResponse,
  OperationalInfrastructureInfo,
} from "@vygruppen/vy-train-map";
import { getTrafficguiBaseUrl } from "api/common";
import { queryFnGET } from "api/tanStackQuery/helpers";
import { queryClient } from "api/tanStackQuery/queryClient";
import { LogLevel, log } from "logging/datadogBrowserLogs";
import { useEffect } from "react";
import { createToast } from "shared/components/Spor";
import { useCountryCodeForDropsRole } from "shared/hooks/useCountryCodeForDropsRole";
import { InfrastructureInformationMessage } from "websocket/websocketSharedWorkerTypes";

const FIVE_MINUTES = 5 * 60 * 1000;
const infrastructureInformationQueryKey = (countryCode?: CountryCode) => [
  "infraInfo",
  countryCode,
];

const updateInfraInfo = (
  message: OperationalInfrastructureInfo,
  prev: OperationalInfrastructureInfo[],
): OperationalInfrastructureInfo[] => {
  const alreadyMatch = prev.some((info) => info.uuid === message.uuid);

  if (message.event.active || (alreadyMatch && !message.event.active)) {
    return [message, ...prev.filter((info) => info.uuid !== message.uuid)];
  }

  return prev;
};

const deleteInfraInfo = (
  message: OperationalInfrastructureInfo,
  prev: OperationalInfrastructureInfo[],
): OperationalInfrastructureInfo[] =>
  prev.filter((info) => info.uuid !== message.uuid);

const handleInfraWebsocketMessage = ({
  message: websocketMessage,
}: InfrastructureInformationMessage) => {
  const { type, message } = websocketMessage;
  const { countryCode } = message.event;
  switch (type) {
    case "UPDATE":
      queryClient.setQueryData<InfrastructureResponse>(
        infrastructureInformationQueryKey(countryCode),
        (prev) =>
          prev
            ? {
                infrastructure_information: updateInfraInfo(
                  message,
                  prev.infrastructure_information,
                ),
              }
            : {
                infrastructure_information: message.event.active
                  ? [message]
                  : [],
              },
      );
      break;
    case "DELETE":
      queryClient.setQueryData<InfrastructureResponse>(
        infrastructureInformationQueryKey(countryCode),
        (prev) =>
          prev
            ? {
                infrastructure_information: deleteInfraInfo(
                  message,
                  prev.infrastructure_information,
                ),
              }
            : {
                infrastructure_information: [],
              },
      );
      break;
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- might get weird data from backend
    default:
      log(
        LogLevel.error,
        handleInfraWebsocketMessage.name,
        `Received message with invalid type: ${type} for opInfo with uuid: ${message.uuid}`,
      );
  }
};

export const useInfrastructureInformation = () => {
  const countryCode = useCountryCodeForDropsRole();

  const {
    data: infraInfo,
    isSuccess: isInfraInfoSuccess,
    status,
  } = useQuery({
    queryKey: infrastructureInformationQueryKey(countryCode),
    retry: 1,
    queryFn: ({ signal }) =>
      queryFnGET<InfrastructureResponse>({
        signal,
        url: `${getTrafficguiBaseUrl()}/trainmap-backend/operational-information/infrastructure-events?countryCode=${countryCode}`,
      }),
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const cleanupOldEvents = setInterval(() => {
      queryClient.setQueryData<InfrastructureResponse>(
        infrastructureInformationQueryKey(countryCode),
        (prev) =>
          prev
            ? {
                infrastructure_information:
                  prev.infrastructure_information.filter(
                    (info) => info.event.active,
                  ),
              }
            : prev,
      );
    }, FIVE_MINUTES);
    return () => clearInterval(cleanupOldEvents);
  }, []);

  useEffect(() => {
    if (status === "error") {
      createToast({
        variant: "critical",
        text: "Kunne ikke hente infrastrukturbrudd",
      });
    }
  }, [status]);

  return {
    infraInfo,
    isInfraInfoSuccess,
    handleInfraWebsocketMessage,
  };
};
